// Where a stack's picture lives, relative to the icons folder and under /icons/ on the web.
// Items share one picture per name and damage: NBT variants use the plain item's picture.
// Fluids imported from NEI are filed by their in-game name (fluid-label/), because NEI's dump
// only knows a fluid by its display name; the dashboard passes the label so the server can find them.

import type { Stack } from "./stack.js";

/** Path segments of the icon file: item/<name>/<damage>.png, fluid/<name>.png, essentia/<name>.png. */
export function iconPathParts(stack: Stack): string[] {
  const name = encodeURIComponent(stack.name);
  if (stack.type === "item") return ["item", name, `${stack.damage ?? 0}.png`];
  return [stack.type, `${name}.png`];
}

/** How a fluid's in-game name is written into a file name: case and spacing don't matter. */
export function iconLabelKey(label: string): string {
  return label.replace(/§./g, "").trim().replace(/\s+/g, " ").toLowerCase();
}

/** Path segments of a fluid picture filed by its in-game name: fluid-label/<label>.png. */
export function fluidLabelPathParts(label: string): string[] {
  return ["fluid-label", `${encodeURIComponent(iconLabelKey(label))}.png`];
}

/** URL the dashboard loads the icon from. The file path's segments are already URL-safe. */
export function iconUrl(stack: Stack): string {
  const url = "/icons/" + iconPathParts(stack).join("/");
  return stack.type === "fluid" && stack.label ? `${url}?label=${encodeURIComponent(stack.label)}` : url;
}

/** Reads an /icons/... path back into a stack, or null when it is not one. */
export function stackFromIconPath(pathname: string): Stack | null {
  const parts = pathname.replace(/^\/icons\//, "").split("/");
  try {
    const seg = parts.map(decodeURIComponent);
    if (seg[0] === "item" && seg.length === 3) {
      const m = /^(\d+)\.png$/.exec(seg[2]);
      return m ? { type: "item", name: seg[1], damage: Number(m[1]) } : null;
    }
    if ((seg[0] === "fluid" || seg[0] === "essentia") && seg.length === 2 && seg[1].endsWith(".png")) {
      return { type: seg[0], name: seg[1].slice(0, -4) };
    }
  } catch {
    // Malformed escapes.
  }
  return null;
}
